/**
 * Builds initial form values for the Appeal Info modal from a table row or API record.
 */

import { CaseAppealsTableRowData } from "../../../../data-model/caseAppealsTable";
import { AppealType, AppealTypeLabels } from "../../../../enums/appealType";
import { resolveAppealStatusValue } from "../../appealSummaryModal/utils/appealStatusOptions";

export interface AppealInfoFormData {
  appealType: string;
  appealStatus: string;
  appealReason: string;
  appealComments: string;
}

/**
 * Maps a stored appeal type onto the frontend `AppealType` enum id used by the form.
 *
 * @param value - Raw appeal type from the API/table row
 * @returns Frontend appeal type id (e.g. `fax`, `phone`)
 */
const normalizeAppealTypeForForm = (value: string | null | undefined): string => {
  if (!value?.trim()) {
    return AppealType.FAX;
  }

  const trimmed = value.trim();
  const normalized = trimmed.toLowerCase().replace(/\s+/g, "_");

  const enumMatch = Object.values(AppealType).find((type) => type === normalized);
  if (enumMatch) {
    return enumMatch;
  }

  const labelMatch = Object.entries(AppealTypeLabels).find(
    ([, label]) => label.toLowerCase() === trimmed.toLowerCase(),
  );
  if (labelMatch) {
    return labelMatch[0];
  }

  if (trimmed === "Call") return AppealType.PHONE;
  if (trimmed === "E-Appeal") return AppealType.PORTAL;
  if (trimmed === "Manual") return AppealType.MAIL;

  return AppealType.FAX;
};

/**
 * Maps a stored appeal status onto the exact backend status string used by the form.
 * Preserves Submitted / Appeal Approved / Appeal Denied instead of collapsing them.
 *
 * @param value - Raw appeal status from the API/table row
 * @returns Backend appeal status string for the dropdown
 */
const normalizeAppealStatusForForm = (
  value: string | null | undefined,
): string => {
  return resolveAppealStatusValue(value) || "Not Started";
};

/**
 * Maps row/API fields into Appeal Info modal form state.
 *
 * @param data - Case Appeals table row or raw API record
 * @returns Prefilled appeal info form values
 */
export const buildAppealInfoInitialData = (
  data: CaseAppealsTableRowData | Record<string, unknown> | null | undefined,
): AppealInfoFormData => {
  if (!data) {
    return {
      appealType: AppealType.FAX,
      appealStatus: "Not Started",
      appealReason: "N/A",
      appealComments: "",
    };
  }

  const record = data as CaseAppealsTableRowData;

  return {
    appealType: normalizeAppealTypeForForm(record.appeal_type),
    appealStatus: normalizeAppealStatusForForm(record.appeal_status),
    appealReason: record.appeal_reason?.trim() || "N/A",
    appealComments: record.appeal_comment ?? "",
  };
};
